import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SITE } from "@/lib/config";
import { LogoFull } from "./Logo";

const BOOT_LINES = [
  { text: "[OK] Loading outcome registry", tone: "ok" },
  { text: "[OK] Connecting to verification layer", tone: "ok" },
  { text: "[OK] Loading consequence graph", tone: "ok" },
  { text: "[OK] Initializing reputation engine", tone: "ok" },
  { text: "[OK] Connecting autonomous economy", tone: "ok" },
  { text: "[OK] PROTOCOL ONLINE", tone: "final" },
] as const;

interface Line {
  text: string;
  tone: "ok" | "final";
}

/** First-load boot sequence. Skippable; flagged in localStorage for returning visitors. */
export function BootSequence({ onComplete }: { onComplete: () => void }) {
  const [lines, setLines] = useState<Line[]>([]);
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const done = useRef(false);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    setLeaving(true);
    try {
      localStorage.setItem(SITE.bootStorageKey, "1");
    } catch {}
    setTimeout(onComplete, 420);
  };

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setLines([{ text: "> INITIALIZING CONSEQUENCE PROTOCOL...", tone: "ok" }]), 200));
    BOOT_LINES.forEach((l, i) => {
      timers.push(
        setTimeout(() => {
          setLines((prev) => [...prev, l as Line]);
          setProgress(((i + 1) / BOOT_LINES.length) * 100);
        }, 550 + i * 260)
      );
    });
    timers.push(setTimeout(finish, 550 + BOOT_LINES.length * 260 + 650));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      data-testid="boot-sequence"
      initial={{ opacity: 1 }}
      animate={{ opacity: leaving ? 0 : 1 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-base"
      onClick={finish}
      role="status"
      aria-label="Consequence Protocol booting"
    >
      <div className="bg-grid absolute inset-0 opacity-40" />
      <div className="relative w-full max-w-xl px-6">
        <div className="mb-8 flex justify-center">
          <LogoFull className="h-28" />
        </div>
        <div className="panel scanlines hud-corners p-5 font-mono text-xs sm:text-sm" data-testid="boot-output">
          {lines.map((l, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.18 }}
              className={l.tone === "final" ? "py-0.5 text-cyan" : "py-0.5 text-dim"}
            >
              {l.text}
            </motion.p>
          ))}
          <p className="term-cursor py-0.5 text-cyan/60">&nbsp;</p>
          <div className="mt-4 h-0.5 w-full bg-navy">
            <div className="h-full bg-cyan transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          <span className="mono-label">Consequence Terminal</span>
          <button
            data-testid="boot-skip-btn"
            className="border border-line px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-dim transition-colors duration-200 hover:border-cyan/60 hover:text-cyan"
          >
            Skip →
          </button>
        </div>
      </div>
    </motion.div>
  );
}
